/**
 * NDA Item Master · Admin CRUD screen.
 *
 * Platform-Setup source of truth for the parent/child NDA catalogue items.
 * Renders items hierarchically by `parent_category` with expand/collapse and
 * child counts.  Rows without a parent surface under an "Unclassified" bucket.
 *
 * Governance:
 *  - Items are NEVER hard-deleted — deactivate toggle removes them from the
 *    NDA Builder picker; historical NDA snapshots stay intact.
 *  - Editing an item affects future NDAs only.
 *  - `nda_master:write` capability gate matches the backend.
 *
 * 2026-02-25 · Hierarchy UI correction — no catalogue architecture changes,
 * no touch to the Annexure Catalogue picker used by Sales, no changes to
 * historical NDA snapshots.
 */
import { useEffect, useMemo, useState } from "react";
import {
    Plus, PencilSimple, Prohibit, CheckCircle, Funnel, Package, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { DialogFooter } from "../../components/ui/dialog";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";

const NDA_TYPES = ["Data", "Software"];
const UNCLASSIFIED = "__unclassified__";
const UNCLASSIFIED_LABEL = "Unclassified";

function ItemDialog({ open, onOpenChange, initial, knownParents, onSaved }) {
    const blank = {
        item_code: "",
        item_name: "",
        nda_type: "Data",
        item_group: "",
        parent_category: "",
        technical_name: "",
        primary_key: "",
        description: "",
        display_order: 100,
        is_active: true,
    };
    const [form, setForm] = useState(blank);
    const [submitting, setSubmitting] = useState(false);
    const isEdit = Boolean(initial?.id);

    useEffect(() => {
        if (!open) return;
        setForm(initial ? { ...blank, ...initial } : blank);
    }, [open, initial]);

    const submit = async () => {
        if (!form.item_name.trim()) { toast.error("Item name is required"); return; }
        if (!isEdit && !form.item_code.trim()) { toast.error("Item code is required"); return; }
        if (!form.description.trim()) { toast.error("Description is required"); return; }
        setSubmitting(true);
        try {
            const shared = {
                item_name: form.item_name.trim(),
                item_group: (form.item_group || "").trim(),
                parent_category: (form.parent_category || "").trim(),
                technical_name: (form.technical_name || "").trim(),
                primary_key: (form.primary_key || "").trim(),
                description: form.description.trim(),
                display_order: Number(form.display_order) || 100,
                is_active: form.is_active,
            };
            if (isEdit) {
                const { data } = await api.patch(`/billing/nda-item-master/${initial.id}`, shared);
                toast.success(`Updated ${data.item_code}`);
                onSaved?.(data);
            } else {
                const { data } = await api.post("/billing/nda-item-master/create", {
                    item_code: form.item_code.trim().toUpperCase(),
                    nda_type: form.nda_type,
                    ...shared,
                });
                toast.success(`Created ${data.item_code}`);
                onSaved?.(data);
            }
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title={isEdit ? `Edit ${initial?.item_code}` : "New NDA Item"}
            description={isEdit
                ? "Changes take effect ONLY for future NDAs.  Existing NDAs keep their per-NDA description snapshot untouched."
                : "Add a confidential-information item.  Set a Parent Category (e.g. Mutual Fund (MF)) to nest it under that group in the NDA Builder."}
            testId="nda-item-master-dialog"
            widthClass="max-w-2xl"
        >
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Item Code {isEdit && <span className="text-xs text-slate-400">(immutable)</span>}</Label>
                    <Input
                        value={form.item_code}
                        onChange={(e) => setForm((f) => ({ ...f, item_code: e.target.value }))}
                        disabled={isEdit}
                        placeholder="e.g. MF__NEWDATASET"
                        data-testid="nda-item-code-input"
                    />
                </div>
                <div>
                    <Label>Display Name *</Label>
                    <Input
                        value={form.item_name}
                        onChange={(e) => setForm((f) => ({ ...f, item_name: e.target.value }))}
                        data-testid="nda-item-name-input"
                    />
                </div>
                <div>
                    <Label>NDA Type {isEdit && <span className="text-xs text-slate-400">(immutable)</span>}</Label>
                    <select
                        value={form.nda_type}
                        onChange={(e) => setForm((f) => ({ ...f, nda_type: e.target.value }))}
                        disabled={isEdit}
                        className="mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-item-type-select"
                    >
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t} NDA</option>)}
                    </select>
                </div>
                <div>
                    <Label>Parent Category</Label>
                    <Input
                        value={form.parent_category}
                        onChange={(e) => setForm((f) => ({ ...f, parent_category: e.target.value }))}
                        list="nda-parent-suggestions"
                        placeholder="e.g. Mutual Fund (MF)"
                        data-testid="nda-item-parent-input"
                    />
                    <datalist id="nda-parent-suggestions">
                        {knownParents.map((p) => <option key={p} value={p} />)}
                    </datalist>
                </div>
                <div>
                    <Label>Technical Name</Label>
                    <Input
                        value={form.technical_name}
                        onChange={(e) => setForm((f) => ({ ...f, technical_name: e.target.value }))}
                        placeholder="e.g. MFNAVHistory"
                        className="font-mono text-xs"
                        data-testid="nda-item-technical-input"
                    />
                </div>
                <div>
                    <Label>Primary Key</Label>
                    <Input
                        value={form.primary_key}
                        onChange={(e) => setForm((f) => ({ ...f, primary_key: e.target.value }))}
                        placeholder="e.g. MFNAVHistoryId"
                        className="font-mono text-xs"
                        data-testid="nda-item-primary-key-input"
                    />
                </div>
                <div>
                    <Label>Item Group</Label>
                    <Input
                        value={form.item_group}
                        onChange={(e) => setForm((f) => ({ ...f, item_group: e.target.value }))}
                        placeholder="e.g. Investment Data"
                        data-testid="nda-item-group-input"
                    />
                </div>
                <div>
                    <Label>Display Order</Label>
                    <Input
                        type="number"
                        min="0"
                        value={form.display_order}
                        onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
                        data-testid="nda-item-order-input"
                    />
                </div>
                <div className="flex items-center gap-2 mt-2 col-span-2">
                    <input
                        id="nda-item-active"
                        type="checkbox"
                        checked={form.is_active}
                        onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                        data-testid="nda-item-active-checkbox"
                    />
                    <Label htmlFor="nda-item-active" className="!mb-0">Active (available in NDA Builder)</Label>
                </div>
                <div className="col-span-2">
                    <Label>Default Description *</Label>
                    <Textarea
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        placeholder="Copied onto new NDAs as the default per-item description."
                        data-testid="nda-item-desc-input"
                    />
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-item-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="nda-item-submit"
                >
                    {submitting ? "Saving…" : isEdit ? "Save changes" : "Create item"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

export default function NDAItemMaster() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState("");
    const [parentFilter, setParentFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("all"); // all / active / inactive
    const [textQuery, setTextQuery] = useState("");
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [expanded, setExpanded] = useState({}); // per-bucket key → bool

    const load = async () => {
        try {
            const params = { include_inactive: true };
            if (typeFilter) params.nda_type = typeFilter;
            const { data } = await api.get("/billing/nda-item-master/list", { params });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load items"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [typeFilter]);

    // Parent-category catalogue (ignoring the search text so the filter stays
    // stable while the user is typing).
    const parentCategories = useMemo(() => {
        const s = new Set(
            items.map((i) => (i.parent_category || "").trim()).filter(Boolean),
        );
        return Array.from(s).sort();
    }, [items]);

    const visible = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        return items.filter((i) => {
            if (parentFilter) {
                if (parentFilter === UNCLASSIFIED) {
                    if ((i.parent_category || "").trim()) return false;
                } else if ((i.parent_category || "").trim() !== parentFilter) {
                    return false;
                }
            }
            if (activeFilter === "active" && !i.is_active) return false;
            if (activeFilter === "inactive" && i.is_active) return false;
            if (!q) return true;
            return (
                (i.item_code || "").toLowerCase().includes(q) ||
                (i.item_name || "").toLowerCase().includes(q) ||
                (i.description || "").toLowerCase().includes(q) ||
                (i.technical_name || "").toLowerCase().includes(q) ||
                (i.primary_key || "").toLowerCase().includes(q) ||
                (i.parent_category || "").toLowerCase().includes(q)
            );
        });
    }, [items, textQuery, parentFilter, activeFilter]);

    // Bucket visible rows by parent_category.  Preserves order within each
    // bucket by display_order → item_name.  Unclassified rows collapse into
    // their own bucket at the bottom.
    const buckets = useMemo(() => {
        const map = new Map();
        for (const row of visible) {
            const key = (row.parent_category || "").trim() || UNCLASSIFIED;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(row);
        }
        for (const arr of map.values()) {
            arr.sort((a, b) => (
                (Number(a.display_order) || 100) - (Number(b.display_order) || 100)
                || (a.item_name || "").localeCompare(b.item_name || "")
            ));
        }
        const named = Array.from(map.entries())
            .filter(([k]) => k !== UNCLASSIFIED)
            .sort(([a], [b]) => a.localeCompare(b));
        if (map.has(UNCLASSIFIED)) named.push([UNCLASSIFIED, map.get(UNCLASSIFIED)]);
        return named;
    }, [visible]);

    const isBucketOpen = (key) => {
        if (Object.prototype.hasOwnProperty.call(expanded, key)) return expanded[key];
        // Default: open when a search is active OR when the bucket is the
        // only one visible; otherwise let the operator open it.
        if (textQuery.trim()) return true;
        return buckets.length === 1;
    };

    const toggleBucket = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !isBucketOpen(key) }));
    };

    const toggleActive = async (row) => {
        try {
            const { data } = await api.patch(`/billing/nda-item-master/${row.id}`, {
                is_active: !row.is_active,
            });
            toast.success(data.is_active ? "Activated" : "Deactivated");
            setItems((prev) => prev.map((r) => (r.id === row.id ? data : r)));
        } catch (err) {
            toast.error(formatApiError(err, "Toggle failed"));
        }
    };

    // Total live-active count per bucket (across the full catalogue, not just
    // the visible slice) so the header count stays authoritative.
    const activeCountByParent = useMemo(() => {
        const map = new Map();
        for (const i of items) {
            const key = (i.parent_category || "").trim() || UNCLASSIFIED;
            const prev = map.get(key) || { total: 0, active: 0 };
            prev.total += 1;
            if (i.is_active) prev.active += 1;
            map.set(key, prev);
        }
        return map;
    }, [items]);

    return (
        <div data-testid="nda-item-master-page">
            <PageHeader
                eyebrow="Admin · NDA Configuration"
                title="NDA Items"
                description="Configurable confidential-information catalogue used by the NDA Builder.  Editing an item affects future NDAs only — historical NDAs keep their per-record snapshot untouched."
                actions={
                    <div className="flex items-center gap-2">
                        <Link to="/admin/nda-templates">
                            <Button variant="outline" className="gap-1.5" data-testid="nda-item-goto-templates">
                                <Package size={14} /> NDA Templates
                            </Button>
                        </Link>
                        <Button
                            onClick={() => { setEditing(null); setDialogOpen(true); }}
                            className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                            data-testid="nda-item-new-btn"
                        >
                            <Plus size={16} weight="bold" /> New Item
                        </Button>
                    </div>
                }
            />
            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="nda-item-filters">
                    <Input
                        value={textQuery}
                        onChange={(e) => setTextQuery(e.target.value)}
                        placeholder="Search by name / code / technical name / primary key / description…"
                        className="max-w-md h-9 text-sm"
                        data-testid="nda-item-search"
                    />
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-item-type-filter"
                    >
                        <option value="">All types</option>
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                        value={parentFilter}
                        onChange={(e) => setParentFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-item-parent-filter"
                    >
                        <option value="">All parent categories</option>
                        {parentCategories.map((p) => <option key={p} value={p}>{p}</option>)}
                        <option value={UNCLASSIFIED}>{UNCLASSIFIED_LABEL}</option>
                    </select>
                    <select
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-item-active-filter"
                    >
                        <option value="all">All</option>
                        <option value="active">Active only</option>
                        <option value="inactive">Inactive only</option>
                    </select>
                </div>
                {loading ? (
                    <div className="text-sm text-slate-500">Loading items…</div>
                ) : buckets.length === 0 ? (
                    <EmptyState
                        icon={<Package size={40} weight="thin" />}
                        title="No items found"
                        description="Adjust the filters or create a new confidentiality item."
                        data-testid="nda-item-empty"
                    />
                ) : (
                    <div className="space-y-3" data-testid="nda-item-buckets">
                        {buckets.map(([key, rows]) => {
                            const label = key === UNCLASSIFIED ? UNCLASSIFIED_LABEL : key;
                            const open = isBucketOpen(key);
                            const activeStats = activeCountByParent.get(key) || { total: rows.length, active: rows.filter((r) => r.is_active).length };
                            return (
                                <div key={key} className="reedos-card overflow-hidden" data-testid={`nda-item-bucket-${key}`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleBucket(key)}
                                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
                                        data-testid={`nda-item-bucket-toggle-${key}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
                                            <span className="font-semibold text-sm text-slate-900">{label}</span>
                                            <span className="text-xs text-slate-500" data-testid={`nda-item-bucket-count-${key}`}>
                                                · {activeStats.active} active
                                                {activeStats.total !== activeStats.active && (
                                                    <> · {activeStats.total - activeStats.active} inactive</>
                                                )}
                                                <> · {rows.length} shown</>
                                            </span>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                            {open ? "Hide" : "Show"} items
                                        </span>
                                    </button>
                                    {open && (
                                        <table className="w-full text-sm" data-testid={`nda-item-table-${key}`}>
                                            <thead className="bg-white border-b border-slate-100">
                                                <tr className="text-left">
                                                    <th className="px-4 py-2 reedos-eyebrow">Display Name</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Technical Name</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Primary Key</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Type</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Parent Category</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Description</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Order</th>
                                                    <th className="px-4 py-2 reedos-eyebrow">Status</th>
                                                    <th className="px-4 py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row) => (
                                                    <tr key={row.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`nda-item-row-${row.id}`}>
                                                        <td className="px-4 py-3 text-slate-900">
                                                            <div className="font-medium">{row.item_name}</div>
                                                            <div className="text-[10px] font-mono text-slate-500">{row.item_code}</div>
                                                        </td>
                                                        <td className="px-4 py-3 font-mono text-xs text-slate-700">
                                                            {row.technical_name || "—"}
                                                        </td>
                                                        <td className="px-4 py-3 font-mono text-xs text-slate-700">
                                                            {row.primary_key || "—"}
                                                        </td>
                                                        <td className="px-4 py-3 text-xs uppercase tracking-wide">{row.nda_type}</td>
                                                        <td className="px-4 py-3 text-xs text-slate-600">{row.parent_category || "—"}</td>
                                                        <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate" title={row.description}>{row.description}</td>
                                                        <td className="px-4 py-3 text-xs text-slate-600">{row.display_order}</td>
                                                        <td className="px-4 py-3">
                                                            {row.is_active
                                                                ? <StatusBadge status="Active">Active</StatusBadge>
                                                                : <StatusBadge status="Cancelled">Inactive</StatusBadge>}
                                                        </td>
                                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                                            <button
                                                                onClick={() => { setEditing(row); setDialogOpen(true); }}
                                                                className="text-slate-700 hover:text-slate-900 mr-3"
                                                                data-testid={`nda-item-edit-${row.id}`}
                                                                title="Edit"
                                                            >
                                                                <PencilSimple size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => toggleActive(row)}
                                                                className={row.is_active ? "text-amber-700 hover:text-amber-900" : "text-emerald-700 hover:text-emerald-900"}
                                                                data-testid={`nda-item-toggle-${row.id}`}
                                                                title={row.is_active ? "Deactivate" : "Activate"}
                                                            >
                                                                {row.is_active ? <Prohibit size={14} /> : <CheckCircle size={14} />}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <ItemDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                initial={editing}
                knownParents={parentCategories}
                onSaved={() => load()}
            />
        </div>
    );
}
